import React, { useState, useEffect } from 'react';
import { Heart, MessageSquare, Send, CornerDownRight, Trash2, User } from '../../lib/lucide-proxy';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BlogInteraction = ({ postId }) => {
  const { user, profile } = useAuth();
  const [comments, setComments] = useState([]);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // ID do comentário pai
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (postId) {
      fetchInteractions();
    }
  }, [postId, user]);

  const fetchInteractions = async () => {
    try {
      // 1. Fetch Likes
      const { count, error: likeError } = await supabase
        .from('blog_likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId);
      
      if (!likeError) setLikesCount(count || 0);

      // Check if current user liked
      if (user) {
        const { data } = await supabase
          .from('blog_likes')
          .select('id')
          .eq('post_id', postId)
          .eq('user_id', user.id)
          .maybeSingle();
        setIsLiked(!!data);
      }

      // 2. Fetch Comments
      const { data: commentsData, error: commentError } = await supabase
        .from('blog_comments')
        .select(`
          *,
          user:users (full_name, avatar_url, role)
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (!commentError) setComments(commentsData || []);

    } catch (error) {
      console.error('Error fetching interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Faça login para curtir!');
      return;
    }

    // Optimistic update
    const previousState = isLiked;
    const previousCount = likesCount;
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);

    try {
      if (previousState) {
        // Unlike
        await supabase.from('blog_likes').delete().eq('post_id', postId).eq('user_id', user.id);
      } else {
        // Like
        await supabase.from('blog_likes').insert([{ post_id: postId, user_id: user.id }]);
      }
    } catch (error) {
      // Revert on error
      setIsLiked(previousState);
      setLikesCount(previousCount);
      toast.error('Erro ao curtir');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Faça login para comentar!');
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('blog_comments')
        .insert([{
          post_id: postId,
          user_id: user.id,
          content: newComment,
          parent_id: replyingTo
        }])
        .select('*, user:users(full_name, avatar_url, role)')
        .single();

      if (error) throw error;

      setComments([...comments, data]);
      setNewComment('');
      setReplyingTo(null);
      toast.success('Comentário enviado!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao enviar comentário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm('Excluir comentário?')) return;
    try {
      await supabase.from('blog_comments').delete().eq('id', commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comentário removido');
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // Group comments into threads (Parent -> Children)
  const threads = comments.filter(c => !c.parent_id).map(parent => ({
    ...parent,
    replies: comments.filter(c => c.parent_id === parent.id)
  }));

  const CommentItem = ({ comment, isReply = false }) => (
    <div className={`flex gap-3 ${isReply ? 'ml-12 mt-3' : 'mt-6'}`}>
      <div className="flex-shrink-0">
        {comment.user?.avatar_url ? (
          <img src={comment.user.avatar_url} alt="Avatar" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
            <User className="w-4 h-4" />
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="bg-gray-50 p-3 rounded-xl rounded-tl-none">
          <div className="flex justify-between items-start">
            <div>
              <span className="font-bold text-sm text-dark-900 mr-2">
                {comment.user?.full_name || 'Usuário'}
              </span>
              {comment.user?.role === 'admin' && (
                <span className="text-[10px] bg-black text-white px-1.5 py-0.5 rounded uppercase font-bold">Admin</span>
              )}
              <span className="text-xs text-gray-400 block sm:inline sm:ml-2">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: ptBR })}
              </span>
            </div>
            {(user?.id === comment.user_id || profile?.role === 'admin') && (
              <button 
                onClick={() => handleDeleteComment(comment.id)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          <p className="text-sm text-dark-700 mt-1 whitespace-pre-wrap">{comment.content}</p>
        </div>
        
        {!isReply && user && (
          <button 
            className="text-xs text-dark-500 font-medium mt-1 ml-1 hover:text-primary-600 transition-colors flex items-center gap-1"
            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
          >
            <CornerDownRight className="w-3 h-3" /> Responder
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="border-t border-gray-100 pt-8 mt-12">
      {/* Likes Section */}
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={handleLike}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
            isLiked 
              ? 'bg-red-50 text-red-500 border border-red-200 shadow-sm' 
              : 'bg-gray-50 text-dark-600 hover:bg-gray-100 border border-transparent'
          }`}
        >
          <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
          <span className="font-bold">{likesCount}</span> 
          <span className="hidden sm:inline">{likesCount === 1 ? 'Curtida' : 'Curtidas'}</span>
        </button>
        
        <div className="flex items-center gap-2 text-dark-500">
          <MessageSquare className="w-5 h-5" />
          <span className="font-bold">{comments.length}</span>
          <span className="hidden sm:inline">{comments.length === 1 ? 'Comentário' : 'Comentários'}</span>
        </div>
      </div>

      {/* Comment Form */}
      <div className="bg-white mb-8">
        <h3 className="text-lg font-bold text-dark-900 mb-4">Discussão</h3>
        {user ? (
          <form onSubmit={handleComment} className="relative">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyingTo ? "Escreva sua resposta..." : "Deixe seu comentário..."}
              className="w-full p-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all resize-none"
              rows={3}
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              {replyingTo && (
                <button 
                  type="button" 
                  onClick={() => setReplyingTo(null)}
                  className="text-xs text-red-500 hover:underline"
                >
                  Cancelar Resposta
                </button>
              )}
              <button 
                type="submit" 
                disabled={submitting || !newComment.trim()}
                className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors shadow-sm"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-gray-50 p-6 rounded-xl text-center border border-gray-100">
            <p className="text-dark-600 mb-3">Faça login para participar da discussão.</p>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/login'}>
              Entrar Agora
            </Button>
          </div>
        )}
      </div>

      {/* Comments List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-4 text-gray-400">Carregando comentários...</div>
        ) : threads.length === 0 ? (
          <p className="text-gray-400 italic text-sm">Nenhum comentário ainda. Seja o primeiro!</p>
        ) : (
          threads.map(thread => (
            <div key={thread.id}>
              <CommentItem comment={thread} />
              {thread.replies.map(reply => (
                <CommentItem key={reply.id} comment={reply} isReply={true} />
              ))}
              
              {/* Reply Form embedded if specifically replying to this thread */}
              {replyingTo === thread.id && user && (
                <div className="ml-12 mt-3 pl-3 border-l-2 border-primary-100">
                  <p className="text-xs text-primary-600 mb-2 font-medium">Respondendo a {thread.user?.full_name}</p>
                  <form onSubmit={handleComment} className="flex gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Sua resposta..."
                      className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary-500 text-sm"
                    />
                    <Button type="submit" size="sm" disabled={submitting}>Enviar</Button>
                  </form>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogInteraction;